import { Heading } from '@assembly-js/design-system';
import clsx from 'clsx';
import { useRef, useState } from 'react';

import { UserRoles } from 'src/constants/userConsts';
import { useFeatureFlags } from 'src/context';
import { AppSettingsModal } from 'src/features/app-settings-modal/AppSettingsModal';
import history from 'src/history';
import { useImmersiveModalParams } from 'src/hooks/useImmersiveModalParams';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { selectCurrentUser } from 'src/store/storeUtils';
import { getEditModuleUrl } from 'src/utils/Redirects';

import { PageTitleMenu } from './PageTitleMenu';
import { PageTitleMenuButton } from './PageTitleMenuButton';
import { RenameAppPopover } from './RenameAppPopover';

interface EditablePageTitleProps {
  moduleId: string;
  defaultTitle: string;
  className?: string;
}

export const EditablePageTitle = ({
  moduleId,
  defaultTitle,
  className,
}: EditablePageTitleProps) => {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const { EnableConsistentAppSettingsLink } = useFeatureFlags();
  const {
    modalType,
    moduleId: modalModuleId,
    appName: modalAppName,
    moduleType: modalModuleType,
    appId: modalAppId,
    open: openModal,
    close: closeModal,
  } = useImmersiveModalParams();

  const { data: { moduleSettings, extensionsSettings } = {} } =
    useGetInstallsQuery();

  const currentUser = useAppSelector(selectCurrentUser);
  const isAdmin =
    currentUser?.fields?.roles?.includes(UserRoles.InternalAdmin) ?? false;

  const module = moduleSettings?.find((m) => m.id === moduleId);
  const extension = extensionsSettings?.[moduleId];
  const title = module?.label ?? defaultTitle;

  const openMenu = () => {
    setMenuAnchorEl(titleRef.current);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  if (!isAdmin) {
    return (
      <Heading size="sm" className={clsx('truncate', className)}>
        {title}
      </Heading>
    );
  }

  return (
    <>
      <PageTitleMenuButton
        ref={titleRef}
        title={title}
        onClick={openMenu}
        className={className}
      />
      <PageTitleMenu
        anchorEl={menuAnchorEl}
        onClose={closeMenu}
        onRename={() => {
          closeMenu();
          setIsRenameOpen(true);
        }}
        onManage={() => {
          closeMenu();
          if (EnableConsistentAppSettingsLink) {
            openModal('app-settings', {
              moduleType: module?.type ?? 'module',
              moduleId,
              ...(extension?.appId ? { appId: extension.appId } : {}),
              ...(title ? { appName: title } : {}),
            });
          } else {
            history.push(
              getEditModuleUrl({
                moduleType: module?.type ?? 'module',
                moduleId,
                appId: extension?.appId,
              }),
            );
          }
        }}
      />
      <RenameAppPopover
        open={isRenameOpen}
        anchorEl={titleRef.current}
        onClose={() => setIsRenameOpen(false)}
        moduleId={moduleId}
      />
      {EnableConsistentAppSettingsLink && modalType === 'app-settings' && (
        <AppSettingsModal
          isOpen={true}
          onClose={closeModal}
          moduleType={modalModuleType}
          moduleId={modalModuleId}
          appId={modalAppId}
          appName={modalAppName}
        />
      )}
    </>
  );
};
